import { Component, HostListener, OnInit, OnDestroy } from "@angular/core";
import { Router } from "@angular/router";
import { EventService } from "../core/services/event.service";
import { AngularFireAuth } from "@angular/fire/compat/auth";
import { AuthService } from "../core/services/auth.service";
import { EncryptionService } from '../core/services/encryption.service';
import { Idle, DEFAULT_INTERRUPTSOURCES } from '@ng-idle/core';
import { Keepalive } from '@ng-idle/keepalive';
import { DataService } from "../core/services/data.service";
import { VersionService } from "../core/services/version.service";
import { LogToS3Service } from "../core/services/log.service";
import { MatDialog } from "@angular/material/dialog";
import { CreateEventLauncherService } from "../create-event-sheet/create-event-launcher.service";
import {
  trigger,
  transition,
  style,
  animate,
  query
} from '@angular/animations';
import { ToastService } from "../core/services/toast.service";
import { AccountStatusService } from '../core/services/account-status.service';
import { AnalyticsService } from '../core/services/analytics.service';
import { reloadApp } from '../core/utils/reload-app';

declare let gtag: (...args: any[]) => void;

export type MyEventsStatus = 'Live' | 'Scheduled' | 'Published';
export type MyEventsOwnership = 'My Events' | 'Shared with Me';

@Component({
  selector: "app-myevents",
  templateUrl: "./myevents.component.html",
  styleUrls: ["./myevents.component.scss"],
  animations: [
    trigger('fadeAnimation', [
      transition(':enter', [
        query('.animated-item', [
          style({ opacity: 0, transform: 'translateX(100%)' }),
          animate('400ms', style({ opacity: 1, transform: 'translateX(0)' })),
        ], { optional: true })
      ])
    ]),
    trigger('pageLoadAnimation', [
      transition('* => *', [])
    ])
  ]
})
export class MyEventsComponent implements OnInit, OnDestroy {
  user: any;
  _eventDetail: any;
  allEvents: any;
  isLoading = true;
  displayTimer: any;
  totalCategories: any;
  min: any;
  localMin: any;
  localSearchMin = 0;
  searchMin = 0;
  searchTerm: any;
  isSearchActive: boolean = false;
  moreCategories = false;
  searchHasMore = false; // the last search page was full, so another page of categories may exist
  isAnyLive: boolean = false;

  // idle/timeout
  idleState = 'Not started.';
  timedOut = false;
  isClicked: boolean = false;
  lastPing?: Date | null | undefined = null;

  showConfirmModal = false;
  showLockedPopup = false;
  lockedPopupIsHost = false;
  isHomePage = true;
  confirmMessage = "Are you sure you want to stop the event?"
  version: string;
  isActive = true;
  _localTerm: any;
  isHost: any;
  stopEventId: any;
  displayName: any = '';
  enc_eventData: any;

  // policy banner data (kept for parity with shared loadEvents() response handling)
  policyData: any;
  showPolicyBanner: boolean = false;
  skipPolicyBanner: boolean = false;

  // ── New UI state ──
  showFilterPanel = false;
  ownershipFilterOptions: MyEventsOwnership[] = ['My Events', 'Shared with Me'];
  activeOwnershipFilters: Set<MyEventsOwnership> = new Set(['My Events', 'Shared with Me']);

  constructor(
    private router: Router,
    private eventService: EventService,
    private afAuth: AngularFireAuth,
    public dataService: DataService,
    private authService: AuthService,
    private encryptionService: EncryptionService,
    private idle: Idle,
    private loggingService: LogToS3Service,
    private keepalive: Keepalive,
    private toastService: ToastService,
    private dialog: MatDialog,
    private versionService: VersionService,
    private accountStatusService: AccountStatusService,
    private analytics: AnalyticsService,
    private createEventLauncher: CreateEventLauncherService
  ) {
    this.version = this.versionService.version;

    this.afAuth.authState.subscribe((user) => {
      if (!this.isActive) return;
      this.user = user;

      const url = this.router.url || '';
      const href = window.location.href;
      const isDemoRoute = url.includes('demo-event') || href.includes('demo-event') ||
        url.includes('demo-direct-gallery') || href.includes('demo-direct-gallery');
      const isDemoSession = localStorage.getItem('isDemoUser') === 'true';
      const isGuestSession = localStorage.getItem('isGuestUser') === 'true';

      if (isDemoRoute) return;

      if (isDemoSession || isGuestSession) {
        this.afAuth.signOut().catch(() => null);
        localStorage.removeItem('isDemoUser');
        localStorage.removeItem('isGuestUser');
        localStorage.removeItem('eventId');
        localStorage.removeItem('eventStatus');
        localStorage.removeItem('event_hash');
        localStorage.removeItem('e');
        localStorage.removeItem('u');
        localStorage.removeItem('user_name');
        localStorage.removeItem('isHost');
        localStorage.removeItem('showTopBar');
        localStorage.removeItem('user');
        this.router.navigateByUrl('/auth/login', { replaceUrl: true });
        return;
      }

      if (user?.email != null) {
        localStorage.setItem('user_email', user?.email?.toString())
      }

      if (user && !user.isAnonymous) {
        const pendingHostRedirect = localStorage.getItem('pendingHostRedirect');
        if (pendingHostRedirect) {
          this.router.navigate(['/host'], { queryParams: { requestId: pendingHostRedirect } });
          return;
        }
      }

      if ((localStorage.getItem('isEventId') == 'true') && user?.isAnonymous == true && !window.location.href.includes('demo-event')) {
        this.router.navigateByUrl("/event-detail/false");
      }
      else if ((localStorage.getItem('eventId')) && user?.isAnonymous == false && !window.location.href.includes('demo-event')) {
        this.router.navigateByUrl("/event-detail/true");
      }
      else {
        if (user) {
          this.displayName = user.displayName;

          if (user.isAnonymous) {
            this.afAuth.signOut();
            this.router.navigateByUrl("/auth/login", { replaceUrl: true });
          } else {
            this.getAllEvents();
          }
        } else {
          localStorage.setItem("user", "null");
          JSON.parse(localStorage.getItem("user")!);
          this.router.navigateByUrl("/auth/login", { replaceUrl: true });
        }
      }
    });

    // idle timeout
    idle.setIdle(3600);
    idle.setTimeout(10);
    idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);

    idle.onIdleEnd.subscribe(() => {
      this.idleState = 'No longer idle.'
      this.reset();
    });

    idle.onTimeout.subscribe(() => {
      this.idleState = 'Timed out!';
      this.timedOut = true;
      localStorage.clear();
      this.authService.signOut();
      this.router.navigateByUrl('/auth/login', { replaceUrl: true });
    });

    idle.onIdleStart.subscribe(() => {
      this.idleState = 'You\'ve gone idle!'
    });

    idle.onTimeoutWarning.subscribe((countdown) => {
      this.idleState = 'You will time out in ' + countdown + ' seconds!'
    });

    keepalive.interval(15);
    keepalive.onPing.subscribe(() => this.lastPing = new Date());

    this.reset();
  }

  /**
   * The shared create sheet, via the launcher: it checks the plan, and the live
   * event passed in here (the button is disabled while one runs, but that's a
   * UI-only guard), then fires the pixels and opens the sheet.
   */
  openCreateEvent() {
    this.createEventLauncher.launch('myevents', this.isAnyLive ? 'live' : null);
  }

  get isMobile(): boolean {
    return window.innerWidth <= 768;
  }

  reset() {
    this.idle.watch();
    this.idleState = 'Started.';
    this.timedOut = false;
  }

  ngOnInit() {
    const isDemoSession = localStorage.getItem('isDemoUser') === 'true' || localStorage.getItem('isGuestUser') === 'true';
    const url = this.router.url || window.location.href;
    const isDemoRoute = url.includes('demo-event') || url.includes('demo-direct-gallery') || url.includes('demo-landing') || url.includes('/guest/');
    if (!isDemoRoute && isDemoSession) {
      this.afAuth.signOut().catch(() => null);
      localStorage.removeItem('isDemoUser');
      localStorage.removeItem('isGuestUser');
      localStorage.removeItem('eventId');
      localStorage.removeItem('e');
      localStorage.removeItem('u');
      localStorage.removeItem('user_name');
      localStorage.removeItem('isHost');
      localStorage.removeItem('showTopBar');
      this.router.navigateByUrl('/auth/login', { replaceUrl: true });
      return;
    }

    this.dataService.showTopBar = true;

    this.skipPolicyBanner = localStorage.getItem('skipPolicyBanner') === 'true' ? true : false;
    if (this.skipPolicyBanner) {
      localStorage.removeItem('skipPolicyBanner');
      localStorage.setItem('showPolicyBanner', 'false');
      this.showPolicyBanner = false;
    }
    if (localStorage.getItem('eventId')) {
      localStorage.removeItem('eventId');
    }
    if (localStorage.getItem('show_whatsapp_policy')) {
      localStorage.removeItem('show_whatsapp_policy');
    }
    if (localStorage.getItem('event_subfolders')) {
      localStorage.removeItem('event_subfolders');
    }
    if (localStorage.getItem('qrImage')) {
      localStorage.removeItem('qrImage');
    }
    if (localStorage.getItem('viewer_qrImage')) {
      localStorage.removeItem('viewer_qrImage');
    }
    if (localStorage.getItem('isAnyLive')) {
      localStorage.removeItem('isAnyLive');
    }
    if (localStorage.getItem('event_name')) {
      localStorage.removeItem('event_name');
    }
    if (localStorage.getItem('e')) {
      localStorage.removeItem('e');
    }
    if (localStorage.getItem('isHost')) {
      localStorage.removeItem('isHost');
    }
    if (localStorage.getItem('inviteData')) {
      localStorage.removeItem('inviteData');
    }
    if (localStorage.getItem('eventData')) {
      localStorage.removeItem('eventData');
    }
    if (localStorage.getItem('isScheduled')) {
      localStorage.removeItem('isScheduled');
    }
    if (localStorage.getItem('qrcode')) {
      localStorage.removeItem('qrcode');
    }
    if (localStorage.getItem('savedHashtags')) {
      localStorage.removeItem('savedHashtags');
    }
    if (localStorage.getItem('name')) {
      localStorage.removeItem('name');
    }
    if (localStorage.getItem('invite_resp')) {
      localStorage.removeItem('invite_resp');
    }
    if (localStorage.getItem('web_qrImage')) {
      localStorage.removeItem('web_qrImage');
    }
    if (localStorage.getItem('web_viewer_qrImage')) {
      localStorage.removeItem('web_viewer_qrImage');
    }
    if (localStorage.getItem('coverImage')) {
      localStorage.removeItem('coverImage');
    }
    if (localStorage.getItem('openPopup')) {
      localStorage.removeItem('openPopup');
    }
    if (localStorage.getItem('formData')) {
      localStorage.removeItem('formData');
    }
    if (localStorage.getItem('isFutureEvent')) {
      localStorage.removeItem('isFutureEvent');
    }

    window.addEventListener('scroll', this.onScroll.bind(this));
  }

  ngOnDestroy() {
    this.isActive = false;
    window.removeEventListener('scroll', this.onScroll.bind(this));
  }

  transform(value: number) {
    var sec_num = value;
    var hours: any = Math.floor(sec_num / 3600);
    var minutes: any = Math.floor((sec_num - (hours * 3600)) / 60);
    var seconds: any = sec_num - (hours * 3600) - (minutes * 60);

    if (hours < 10) { hours = 0; }
    if (minutes < 10) { minutes = 0; }
    if (seconds < 10) { seconds = 0; }
    this.displayTimer = hours + ':' + minutes + ':' + seconds;
  }

  timer(minutes: number) {
    let seconds: number = minutes * 60;

    const timer = setInterval(() => {
      const hours = Math.floor(seconds / 3600);
      const minutesLeft = Math.floor((seconds % 3600) / 60);
      const secondsLeft = seconds % 60;

      const hoursText = hours < 10 ? '0' + hours : hours.toString();
      const minutesText = minutesLeft < 10 ? '0' + minutesLeft : minutesLeft.toString();
      const secondsText = secondsLeft < 10 ? '0' + secondsLeft : secondsLeft.toString();

      this.displayTimer = `${hoursText}:${minutesText}:${secondsText}`;

      if (seconds === 0) {
        clearInterval(timer);
      } else {
        seconds--;
      }
    }, 1000);
  }

  timestamp() {
    return new Date().getTime();
  }

  onHostImageError(event: any) {
    event.hostImageError = true;
  }

  async getAllEvents() {
    this.min = 0;
    this.isLoading = true;
    this.loggingService.log('Calling apis for myevents page : getTotalCategory');
    (this.eventService.getTotalCategory()).subscribe({
      next: (res) => {
        let resp: any;
        resp = res;
        if (res) {
          this.totalCategories = resp.totalCategories;
          if (resp.mobile !== null || resp.mobile !== 'null') {
            localStorage.setItem("user_phone", 'true');
          }
          this.loggingService.log("getTotalCategory:" + resp)
        } else {
          this.totalCategories = {};
        }
      },
      error: (err) => {
        // console.error(err); //__CONSOLE_LOG__
        this.loggingService.log("getTotalCategory:" + err)
      },
      complete: () => {
        this.isLoading = false;
      }
    });

    this.loadEvents();
  }

  @HostListener('scroll', ['$event'])
  onScroll(event: Event) {
    if (!this.isActive) return;

    event.stopPropagation();
    if (!this.isSearchActive) {
      if (this._eventDetail) {
        if (this._eventDetail.length < this.totalCategories && this.localMin != this.min) {
          this.moreCategories = true;
          const windowHeight = window.innerHeight;
          const documentHeight = document.documentElement.scrollHeight;
          const scrollPosition = window.scrollY;

          if (windowHeight + scrollPosition >= (documentHeight - 1)) {
            this.loadEvents();
          }
        }
        else {
          this.moreCategories = false;
        }
      }
    }
    else {
      if (this._eventDetail) {
        if (this.searchHasMore && this.localSearchMin != this.searchMin) {
          this.moreCategories = true;
          const windowHeight = window.innerHeight;
          const documentHeight = document.documentElement.scrollHeight;
          const scrollPosition = window.scrollY;

          if (windowHeight + scrollPosition >= (documentHeight - 1)) {
            this.onSearch(this.searchTerm);
          }
        }
        else {
          this.moreCategories = false;
        }
      }
    }
  }

  async loadEvents() {
    this.loggingService.log("loadEvents() called");
    this.localMin = 0;
    if (this._eventDetail != undefined && this._eventDetail.length >= this.totalCategories) {
      this.moreCategories = false;
      return;
    }
    this.localMin = this.min;
    this.loggingService.log("calling api getHomePageData");

    (await this.eventService.getHomePageData('10', this.min)).subscribe({
      next: (res: any) => {
        if (res) {
          this.loggingService.log("getHomePageData: " + res);
          if (res == "") {
            this.moreCategories = false;
          }
          let result = res.result;
          if (res.showPolicyBanner == true) {
            if (localStorage.getItem('skipPolicyBanner') === 'true') {
              localStorage.removeItem('skipPolicyBanner');
              localStorage.setItem('showPolicyBanner', 'false');
              this.showPolicyBanner = false;
            } else {
              localStorage.setItem('showPolicyBanner', 'true');
              this.policyData = res.policy_data[0];
              if (localStorage.getItem('showPolicyBanner') !== 'false') {
                this.showPolicyBanner = true;
              }
            }
          }
          if (result.length == 0) {
            this.moreCategories = false;
          }
          else if (result.length == 10) {
            this.moreCategories = true;
          }

          if (!this._eventDetail) {
            this._eventDetail = result;

            this.dataService.userName = res.userName;
            this.dataService.userId = result[0]?.user_id;
            this.allEvents = result;

            if (this._eventDetail.some((item: { categoryName: string; }) => item.categoryName == 'Live')) {
              let liveEvent = this._eventDetail.filter((item: { categoryName: any; }) => item.categoryName);
              this.isAnyLive = true;
              localStorage.setItem('isAnyLive', 'true');
              let end_time = liveEvent[0].events[0].scheduled_end_time;

              const scheduledEndTime = new Date(end_time).getTime();
              const startTime = Date.now();

              let timeDifference: any;
              timeDifference = scheduledEndTime - startTime;

              const hours = Math.floor(timeDifference / (1000 * 60 * 60));
              const minutes = Math.floor((timeDifference % (1000 * 60 * 60)) / (1000 * 60));
              const seconds = Math.floor((timeDifference % (1000 * 60)) / 1000);

              const timeInterval = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;

              const [hoursStr, minutesStr, secondsStr] = timeInterval.split(':');

              const _hours = parseInt(hoursStr, 10);
              const _minutes = parseInt(minutesStr, 10);
              const _seconds = parseInt(secondsStr, 10);

              const totalMinutes = _hours * 60 + _minutes + (_seconds > 0 ? 1 : 0);

              this.timer(totalMinutes);
            }

          } else {
            this._eventDetail = this._eventDetail.concat(result);
            this.allEvents = this.allEvents.concat(result);
          }
          this.min += 10;
          if (this.localMin != this.min) {
            this.eventService.eventData.next(this._eventDetail);
            this.eventService.eventData.next(this.allEvents);
            this.isLoading = false;
          }

        } else {
          this.isLoading = false;
          this.moreCategories = false;
        }
      },
      error: (err) => {
        this.loggingService.log("getHomePageData error: " + err);
        // console.error(err); //__CONSOLE_LOG__
        this.isLoading = false;
        this.moreCategories = false;
      },
    });
  }

  getAllEventsByCategory(category_id: any) {
    localStorage.setItem("category", category_id);
    this.router.navigateByUrl("view-event");
  }

  navigateToRenew(): void {
    this.showLockedPopup = false;
    this.router.navigate(['/choose-subscription-v2']);
  }

  cardDetail(eventId: string, isPast: boolean, restricted: boolean, start_time: any, categoryName: any, default_uplaod: any, isHost: any, event: any) {
    if (event?.locked == 1 || event?.locked === true) {
      this.lockedPopupIsHost = !!isHost;
      this.showLockedPopup = true;
      return;
    }
    const planAlertLevel = this.accountStatusService.snapshot?.plan?.alert_level;
    if (planAlertLevel === 'expired') {
      const isNonPast = categoryName === 'Live' ||
        (categoryName === 'upcoming events' && Date.parse(start_time) > Date.now());
      if (isNonPast) {
        this.toastService.showToast('Your plan has expired. You can only view past events.', 4000);
        return;
      }
    }
    this.loggingService.log("isHost:" + isHost);
    this.loggingService.log("event clicked: " + eventId);

    gtag('event', 'open_event_gallery', {
      event_category: 'user_action',
      event_label: 'Event Page',
      value: 1
    });

    this.clearServiceWorkerCache();
    event.startTime = event.start_time;

    this._eventDetail = null;
    this.allEvents = null;
    this.enc_eventData = this.encryptionService.encryptData(JSON.stringify(event));
    localStorage.setItem('eventData', this.enc_eventData);

    this.loggingService.log("eventData set: " + JSON.stringify(event));
    localStorage.setItem("eventId", eventId);
    localStorage.setItem("isHost", isHost);
    this.loggingService.log("is event restricted?: " + restricted);

    if (categoryName == "upcoming events" && Date.parse(start_time) > Date.now()) {
      localStorage.setItem('isScheduled', 'true');
      localStorage.setItem('isHost', isHost);
    }
    else if (categoryName == "Live" && Date.parse(start_time) < Date.now()) {
      localStorage.setItem('isLive', 'true');
    }
    else {
      localStorage.setItem('isScheduled', 'false');
    }

    if (restricted == true) {
      localStorage.setItem("access", 'restricted');
      this.loggingService.log("navigate to: /my-photos/" + eventId);
      this.router.navigateByUrl("/my-photos/" + eventId);
    } else {
      this.loggingService.log("navigate to: /event-detail");
      this.router.navigateByUrl("/event-detail/" + isPast + "?t:" + this.timestamp());
    }
  }

  clearServiceWorkerCache() {
    if ('caches' in window) {
      caches.keys().then((names) => {
        names.forEach((name) => {
          caches.delete(name);
        });
      });
    }
  }

  logout() {
    this.loggingService.log("log-out called");
    this.authService
      .signOut()
      .then((res) => {
        this.dataService.showTopBar = false;
        this.router.navigateByUrl("/auth/login", { replaceUrl: true });
      })
      .catch((err) => {
        // console.error(err); //__CONSOLE_LOG__
      });
  }

  async onSearch(searchTerm: any) {
    if (this._localTerm !== searchTerm) {
      this.searchMin = 0;
    }

    this.loggingService.log("search called");
    this.isSearchActive = true;
    this._localTerm = searchTerm;
    if (searchTerm) {
      this.localSearchMin = 0;
      // Paging only: stop once the last page came back short. (Comparing the list on
      // screen with the total number of categories is wrong for a search - that total
      // counts every category, not the ones that match.)
      if (this.searchMin > 0 && !this.searchHasMore) {
        this.moreCategories = false;
        return;
      }
      this.localSearchMin = this.searchMin;
      if (this.searchMin == 0) {
        this._eventDetail = [];
        this.isLoading = true;
        this.moreCategories = false;
        this.searchHasMore = false;
      }
      this.loggingService.log("calling search api for: " + searchTerm);
      (await this.eventService.getSearchData('10', this.searchMin, searchTerm)).subscribe({
        next: (res) => {
          // A slow answer for an earlier term must not land in the current search
          if (this._localTerm !== searchTerm || !this.isSearchActive) {
            return;
          }
          // The Lambda answers failures with an { error } object, which is not a page of results
          if (Array.isArray(res)) {
            this.loggingService.log(" search api resp: " + res);
            // A full page of categories means there may be more; running-event groups carry no categoryId
            this.searchHasMore = res.filter((group: any) => group?.categoryId).length >= 10;
            if (res.length === 0 || !this.searchHasMore) {
              this.moreCategories = false;
            }
            if (!this._eventDetail) {
              this._eventDetail = res;
            } else {
              this._eventDetail = this._eventDetail.concat(res);
            }
            this.searchMin += 10;
            if (this.localSearchMin != this.searchMin) {
              this.eventService.eventData.next(this._eventDetail);
              this.isLoading = false;
            }

          } else {
            this.isLoading = false;
            this.moreCategories = false;
            this.searchHasMore = false;
          }
        },
        error: (err) => {
          // console.error(err); //__CONSOLE_LOG__
          this.isLoading = false;
          this.moreCategories = false;
          this.searchHasMore = false;
        },
      });

      this.isClicked = true;
      setTimeout(() => {
        this.isClicked = false;
      }, 300);
    }
    else {
      this.clearSearch();
    }
  }

  clearSearch() {
    this.searchTerm = '';
    this.searchMin = 0;
    this._eventDetail = this.allEvents;
    this.isSearchActive = false;
    this.moreCategories = false;
    this.searchHasMore = false;
  }

  stopConfirm(event: Event, isHost: any, eventId: any) {
    this.loggingService.log("stop event clicked ");
    this.isHost = isHost;
    this.stopEventId = eventId;
    event.stopPropagation();
    this.confirmMessage = "Are you sure you want to stop this event?"
    this.showConfirmModal = true;
  }

  showToast(message: any) {
    this.toastService.showToast(message);
  }

  async stopEvent(event_id: any, isHost: any) {
    this.loggingService.log("stop event confirmed and calling stopEvent api ");
    (await this.eventService.stopEvent(event_id, this.isHost)).subscribe({
      next: (res) => {
        this.loggingService.log("stop event api resp:  " + res);
        if (res) {
          // M-3 gap: event_created fires, but nothing previously tracked when an
          // event ends, so event duration was unmeasurable.
          this.analytics.capture('event_stopped', {
            event_id,
            role: isHost ? 'host' : 'guest',
          });
          localStorage.removeItem('isAnyLive');
          this.showToast('Event stopped successfully!');
          reloadApp('live_event_stopped');
          return;
        }
      }
    });
  }

  onStopConfirmed(confirmed: any, eventId: any) {
    if (confirmed) {
      this.stopEvent(eventId, this.isHost);
    }
    this.showConfirmModal = false;
  }

  // ── New UI helpers ──

  toggleFilterPanel() {
    this.showFilterPanel = !this.showFilterPanel;
  }

  toggleOwnershipFilter(ownership: MyEventsOwnership) {
    if (this.activeOwnershipFilters.has(ownership)) {
      if (this.activeOwnershipFilters.size === 1) return;
      this.activeOwnershipFilters.delete(ownership);
    } else {
      this.activeOwnershipFilters.add(ownership);
    }
  }

  isOwnershipFilterActive(ownership: MyEventsOwnership): boolean {
    return this.activeOwnershipFilters.has(ownership);
  }

  getEventOwnership(event: any): MyEventsOwnership {
    return event?.isHost ? 'My Events' : 'Shared with Me';
  }

  getSectionTitle(item: any): string {
    const categoryName = (item?.categoryName || '').toLowerCase();
    if (categoryName === 'recent events') return 'Recent Activity';
    if (categoryName === 'live') return 'Live Now';
    if (categoryName === 'upcoming events') return 'Upcoming Events';
    return item?.categoryName;
  }

  getEventDate(event: any): Date {
    const start = event?.start_time;
    if (start === null || start === undefined) return new Date();
    if (typeof start === 'number') {
      return new Date(start < 1e12 ? start * 1000 : start);
    }
    const parsed = Date.parse(start);
    return isNaN(parsed) ? new Date() : new Date(parsed);
  }

  getEventStatus(item: any, event: any): MyEventsStatus {
    if (item?.categoryName === 'Live') return 'Live';
    if (item?.categoryName === 'upcoming events' && this.getEventDate(event).getTime() > Date.now()) {
      return 'Scheduled';
    }
    return 'Published';
  }

  getVisibleEvents(item: any): any[] {
    return (item?.events || []).filter((event: any) => this.activeOwnershipFilters.has(this.getEventOwnership(event)));
  }

  // True when some category matched more events than the search returned for it
  searchIsTruncated(): boolean {
    return (this._eventDetail || []).some((item: any) => item?.totalEvents > (item?.events?.length || 0));
  }

  getVisibleSearchEvents(): any[] {
    // Search results arrive grouped by category; flatten them (an event can sit in
    // more than one group) so each event is drawn once.
    const seen = new Set<any>();
    const events: any[] = [];
    for (const item of this._eventDetail || []) {
      for (const event of item?.events || []) {
        if (seen.has(event.eventId)) continue;
        seen.add(event.eventId);
        events.push(event);
      }
    }
    return events.filter((event: any) => this.activeOwnershipFilters.has(this.getEventOwnership(event)));
  }
}
